import React from 'react';
import Image from 'next/legacy/image';
import cx from 'classnames';
import { useRouter } from 'next/router';

import { ILink } from '../../../../../../types/contentful';

import { NavItem } from '../../../shared';
import Link from '../../../../Link';

import icChevronDown from '@/images/ic_chevron_down.svg';
import { IsNewBadge } from '~/components/Navbar/isNewBadge';

interface Props extends NavItem {
  titleExpanded: string;
  setTitleExpanded: (title: string | undefined) => void;
  toggleModal?: () => void;
}
export function ModalNavCategory({
  name,
  title,
  url,
  links,
  tabletLinks,
  mobileLinks,
  titleExpanded,
  setTitleExpanded,
  toggleModal
}: Props) {
  const router = useRouter();
  const expanded = name === titleExpanded;
  const isMeganav = tabletLinks && mobileLinks;
  const ifLinks = links || tabletLinks || mobileLinks;

  const toggleCategoryDisplay = (
    e: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    e.preventDefault();
    if (titleExpanded === name) {
      return setTitleExpanded(undefined);
    }
    setTitleExpanded(name);
  };

  const handleLinkClick = (linkUrl: string) => {
    if (typeof window === 'undefined' || !toggleModal) return;

    const normalizePath = (path: string) => {
      const withoutQuery = path.split('?')[0].split('#')[0];
      return withoutQuery.endsWith('/') && withoutQuery !== '/'
        ? withoutQuery.slice(0, -1)
        : withoutQuery;
    };

    const currentPath = normalizePath(router.asPath);
    const linkPath = normalizePath(linkUrl);

    if (currentPath === linkPath) {
      toggleModal();
    }
  };

  const isSpacerColumn = (title?: string, url?: string) => {
    return title === 'Spacer Column' || url === '#';
  };

  return (
    <div className="group mx-4 my-6 border-b-[2px] border-b-[#F1F1F1] first-of-type:mt-0 last-of-type:border-b-0">
      <div className="mb-6 flex items-center justify-between">
        <Link
          aria-controls={`nav-expanded-content-${name}`}
          aria-expanded={expanded}
          href={ifLinks ? '#0' : (url as string)}
          {...(ifLinks && { onClick: toggleCategoryDisplay })}
          {...(!ifLinks && { onClick: () => handleLinkClick(url as string) })}
          className={cx(
            '-ml-1 flex w-full items-center justify-between rounded-button-shape pl-1 text-[20px] font-medium text-[#1d252c] transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-accent4-base focus-visible:ring-offset-1',
            {
              'no-underline': ifLinks,
              'active:underline': !ifLinks
            }
          )}
        >
          {name || title}
          {ifLinks && (
            <div className="p-0">
              <Image
                alt="Expand navigation list"
                src={icChevronDown}
                className={cx(
                  'transition-transform duration-300 ease-[cubic-bezier(0,0.7,0.3,1)]',
                  { 'rotate-180': expanded, 'rotate-0': !expanded }
                )}
              />
            </div>
          )}
        </Link>
      </div>
      <div
        className={cx('flex flex-col flex-wrap pb-4', {
          flex: expanded,
          hidden: !expanded
        })}
        id={`nav-expanded-content-${name}`}
      >
        {isMeganav ? (
          <>
            {tabletLinks &&
              (() => {
                const filteredLinks = tabletLinks.filter(
                  (link: any) =>
                    !isSpacerColumn(link.fields?.title, link.fields?.url)
                );

                const groupedLinks: Array<{
                  subcategoryLabel?: string;
                  links: Array<{
                    fields: {
                      title: string;
                      url: string;
                      subcategoryLabel?: string;
                      isNew?: boolean;
                      isNewLabel?: string;
                    };
                    sys: any;
                  }>;
                }> = [];

                let currentGroup: {
                  subcategoryLabel?: string;
                  links: Array<any>;
                } | null = null;

                filteredLinks.forEach((link: any) => {
                  const subcategoryLabel = link.fields?.subcategoryLabel;

                  if (
                    !currentGroup ||
                    currentGroup.subcategoryLabel !== subcategoryLabel
                  ) {
                    if (currentGroup) {
                      groupedLinks.push(currentGroup);
                    }
                    currentGroup = {
                      subcategoryLabel,
                      links: [link]
                    };
                  } else {
                    currentGroup.links.push(link);
                  }
                });

                if (currentGroup) {
                  groupedLinks.push(currentGroup);
                }

                return (
                  <div
                    data-testid="meganav-links"
                    className={cx(
                      'm-0 w-full py-4 tiny:hidden tablet:flex tablet:flex-row tablet:flex-wrap tablet:gap-x-12',
                      {
                        'tablet:justify-center': groupedLinks.length > 1,
                        'tablet:justify-start': groupedLinks.length === 1
                      }
                    )}
                  >
                    {groupedLinks.map((group, groupIdx) => (
                      <div
                        key={`tablet-group-${
                          group.subcategoryLabel || 'no-label'
                        }-${groupIdx}`}
                        className="break-inside-avoid"
                      >
                        {group.subcategoryLabel && (
                          <p className="!typography-title-4 mb-3 leading-6">
                            {group.subcategoryLabel}
                          </p>
                        )}
                        <ul className="m-0 list-none">
                          {group.links.map(
                            (
                              {
                                fields: { title, url, isNew, isNewLabel },
                                sys
                              }: any,
                              idx: number
                            ) => (
                              <li
                                className="mb-3"
                                key={`tablet-${sys?.id || title}-${idx}`}
                              >
                                <Link
                                  href={url}
                                  onClick={() => handleLinkClick(url)}
                                  className={cx(
                                    'mt-0 text-[14px] font-normal text-[#1d252c] no-underline',
                                    { '!mb-0 flex items-center gap-1': isNew }
                                  )}
                                >
                                  {title}
                                  <IsNewBadge
                                    isNew={isNew}
                                    isNewLabel={isNewLabel}
                                  />
                                </Link>
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    ))}
                  </div>
                );
              })()}
            <div
              className={cx(
                'm-0 mb-[13px] w-full group-first-of-type:-mb-[7px] tiny:grid tiny:gap-y-6 tablet:hidden',
                {
                  'tiny:grid-cols-1': mobileLinks
                    ? mobileLinks.filter(
                        (link: any) =>
                          !isSpacerColumn(link.fields?.title, link.fields?.url)
                      ).length < 5
                    : false,
                  'tiny:grid-cols-2': mobileLinks
                    ? mobileLinks.filter(
                        (link: any) =>
                          !isSpacerColumn(link.fields?.title, link.fields?.url)
                      ).length >= 5
                    : false
                }
              )}
            >
              {mobileLinks &&
                (() => {
                  const filteredLinks = mobileLinks.filter(
                    (link: any) =>
                      !isSpacerColumn(link.fields?.title, link.fields?.url)
                  );

                  const groupedLinks: Array<{
                    subcategoryLabel?: string;
                    links: Array<{
                      fields: {
                        title: string;
                        url: string;
                        subcategoryLabel?: string;
                        isNew?: boolean;
                        isNewLabel?: string;
                      };
                      sys: any;
                    }>;
                  }> = [];

                  let currentGroup: {
                    subcategoryLabel?: string;
                    links: Array<any>;
                  } | null = null;

                  filteredLinks.forEach((link: any) => {
                    const subcategoryLabel = link.fields?.subcategoryLabel;

                    if (
                      !currentGroup ||
                      currentGroup.subcategoryLabel !== subcategoryLabel
                    ) {
                      if (currentGroup) {
                        groupedLinks.push(currentGroup);
                      }
                      currentGroup = {
                        subcategoryLabel,
                        links: [link]
                      };
                    } else {
                      currentGroup.links.push(link);
                    }
                  });

                  if (currentGroup) {
                    groupedLinks.push(currentGroup);
                  }

                  return groupedLinks.map((group, groupIdx) => (
                    <div
                      key={`mobile-group-${
                        group.subcategoryLabel || 'no-label'
                      }-${groupIdx}`}
                      className="break-inside-avoid"
                    >
                      {group.subcategoryLabel && (
                        <p className="!typography-title-4 mb-2 leading-6">
                          {group.subcategoryLabel}
                        </p>
                      )}
                      <ul className="m-0 list-none">
                        {group.links.map(
                          (
                            {
                              fields: { title, url, isNew, isNewLabel },
                              sys
                            }: any,
                            idx: number
                          ) => (
                            <li
                              className="mb-2"
                              key={`mobile-${sys?.id || title}-${idx}`}
                              data-testid={`mobile-link-${title ?? idx}`}
                            >
                              <Link
                                href={url}
                                onClick={() => handleLinkClick(url)}
                                className={cx(
                                  'mt-0 text-[14px] font-normal text-[#1d252c] no-underline',
                                  {
                                    '!mb-0 !mt-0 flex items-center gap-1': isNew
                                  }
                                )}
                              >
                                {title}
                                <IsNewBadge
                                  isNew={isNew}
                                  isNewLabel={isNewLabel}
                                />
                              </Link>
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  ));
                })()}
            </div>
          </>
        ) : (
          <ul
            className={cx(
              'm-0 mb-[13px] inline-block w-full list-none group-first-of-type:-mb-[7px] tiny:inline-block tiny:columns-2',
              {
                'tiny:columns-1': links
                  ? links.filter(
                      (link) =>
                        !isSpacerColumn(link.fields?.title, link.fields?.url)
                    ).length < 5
                  : false
              }
            )}
          >
            {links &&
              links
                .filter(
                  (link) =>
                    !isSpacerColumn(link.fields?.title, link.fields?.url)
                )
                .map(({ fields, sys }: ILink, idx: number) => (
                  <li
                    key={`link-${sys?.id || fields?.title}-${idx}`}
                    data-testid="nav-link"
                    className="mb-2"
                  >
                    <Link
                      href={fields?.url}
                      onClick={() => handleLinkClick(fields?.url)}
                      className="mt-0 text-[14px] font-normal text-[#1d252c] no-underline"
                    >
                      {fields?.title}
                    </Link>
                  </li>
                ))}
          </ul>
        )}
      </div>
    </div>
  );
}
